import React, { useState, useEffect, useMemo } from "react";
import styles from "./FilterSection.module.css";
import { StoreService } from "../../services/store-service";
import NotFound from "./NotFound";


interface FilterSectionProps {
  selectedService: string | null;
  selectedPartner: string | null;
  selectedDateISO: string | null;
  onFilterChange: (categoryId: number | null, partnerId: number | null) => void;
  services: { id: number; name: string }[];
  partners: { id: number; name: string; services?: number[] }[];
  hasReservations: boolean;
}

interface DropdownProps {
  title: string;
  items: { id: number; name: string }[];
  selectedItem: number | null;
  onItemSelect: (item: number | null) => void;
  isOpen: boolean;
  toggleDropdown: () => void;
}

const Dropdown: React.FC<DropdownProps> = ({
  title,
  items,
  selectedItem,
  onItemSelect,
  isOpen,
  toggleDropdown,
}) => {
  return (
    <div className={styles.dropdown}>
      <button className={styles.button} onClick={toggleDropdown}>
        <span className={`${styles.icon} ${isOpen ? styles.iconOpen : ""}`}>&lt;</span>
        <span>
          {selectedItem !== null ? items.find((i) => i.id === selectedItem)?.name : title}
        </span>
      </button>
      {isOpen && (
        <ul className={styles.list}>
          <li className={styles.listItem} onClick={() => onItemSelect(null)}>
            <span>همه</span>
          </li>
          {items.map((item) => (
            <li
              key={item.id}
              className={`${styles.listItem} ${selectedItem === item.id ? styles.listItemActive : ""}`}
              onClick={() => onItemSelect(item.id)}
            >
              <span>{item.name}</span>
              {selectedItem === item.id && <span className={styles.selectedIcon}>✔</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const FilterSection: React.FC<FilterSectionProps> = ({
  selectedService,
  selectedPartner,
  onFilterChange,
  services,
  partners,
  hasReservations,
}) => {
  const [openDropdown, setOpenDropdown] = useState<"services" | "partners" | null>(null);

  // مقدار معتبر از `selectedService` و `selectedPartner`
  const validService = useMemo(
    () => (selectedService && !isNaN(Number(selectedService)) ? Number(selectedService) : null),
    [selectedService]
  );
  const validPartner = useMemo(
    () => (selectedPartner && !isNaN(Number(selectedPartner)) ? Number(selectedPartner) : null),
    [selectedPartner]
  );

  const [localService, setLocalService] = useState<number | null>(validService);
  const [localPartner, setLocalPartner] = useState<number | null>(validPartner);
  const [filterClicked, setFilterClicked] = useState(false);

  useEffect(() => {
    setLocalService(validService);
    setLocalPartner(validPartner);
  }, [validService, validPartner]);

  // به‌روزرسانی فیلترها و ذخیره در StoreService
  const updateFilter = (categoryId: number | null, partnerId: number | null) => {
    setLocalService(categoryId);
    setLocalPartner(partnerId);
    setFilterClicked(true);
    onFilterChange(categoryId, partnerId);

    if (categoryId !== null) {
      StoreService.SetData("categoryId", categoryId);
    } else {
      localStorage.removeItem("categoryId");
    }

    if (partnerId !== null) {
      StoreService.SetData("partnerId", partnerId);
    } else {
      localStorage.removeItem("partnerId");
    }
  };

  // باز و بسته کردن دراپ‌داون
  const toggleDropdown = (dropdown: "services" | "partners" | null) => {
    setOpenDropdown((prev) => (prev === dropdown ? null : dropdown));
  };

  return (
    <div className={styles.container}>
      <Dropdown
        title="سرویس‌ها"
        items={services}
        selectedItem={localService}
        onItemSelect={(service) => {
          updateFilter(service, localPartner);
          toggleDropdown(null);
        }}
        isOpen={openDropdown === "services"}
        toggleDropdown={() => toggleDropdown("services")}
      />

      <Dropdown
        title="آرایشگر"
        items={partners}
        selectedItem={localPartner}
        onItemSelect={(partner) => {
          updateFilter(localService, partner);
          toggleDropdown(null);
        }}
        isOpen={openDropdown === "partners"}
        toggleDropdown={() => toggleDropdown("partners")}
      />

      {filterClicked && !hasReservations && (
        <NotFound
          type="noService"
          selectedService={services.find((s) => s.id === localService)?.name}
          selectedPartner={partners.find((p) => p.id === localPartner)?.name}
      />
      )}

    </div>
  );
};

export default FilterSection;
